// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Repeats the original set until the track covers the visible width plus one full set,
// then loops by exactly one set's width (measured from the first item to its first copy,
// so the gap between the last item and the next first item matches every other gap).
// Respects prefers-reduced-motion (no animation) and pauses on hover. One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #originals = null;
  #observer = null;
  #width = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal: this.#ac.signal });

    this.addEventListener("mouseenter", () => this.#animation?.pause(), { signal: this.#ac.signal });
    this.addEventListener("mouseleave", () => this.#animation?.play(), { signal: this.#ac.signal });

    this.#observer = new ResizeObserver(() => {
      if (this.clientWidth !== this.#width) this.#setup();
    });
    this.#observer.observe(this);

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#animation?.cancel();
  }

  #setup() {
    const track = this.querySelector("[data-marquee-track]");
    if (!track) return;

    // Keep the progress so a resize doesn't visibly restart the loop.
    const progress = this.#animation?.effect?.getComputedTiming().progress ?? 0;
    this.#animation?.cancel();
    this.#animation = null;
    this.#width = this.clientWidth;

    if (!this.#originals) this.#originals = [...track.children];
    track.replaceChildren(...this.#originals);

    if (this.#mql.matches || this.#originals.length === 0) return;

    const first = this.#originals[0];
    const addSet = () => {
      const copies = this.#originals.map((n) => {
        const clone = n.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        return clone;
      });
      track.append(...copies);
      return copies[0];
    };

    // One set's width = distance from the first item to the start of its first copy.
    const firstCopy = addSet();
    const distance = firstCopy.offsetLeft - first.offsetLeft;
    if (distance <= 0) return;

    // Add copies until there's no empty space at the end of the strip.
    let guard = 0;
    while (track.scrollWidth < this.clientWidth + distance && guard < 50) {
      addSet();
      guard += 1;
    }

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    if (progress) this.#animation.currentTime = progress * durationMs;
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
